import { RETURN } from '../../../constants/tripTypesConstants';
import { getAgesOfChildren, getAgesOfInfants } from '../../../utils/format';

const removeDuplicates = (tripInfo) => {
  const finalTripInfo = {};
  for (const [key, value] of Object.entries(tripInfo)) {
    finalTripInfo[key] = Array.from(new Set(value)).join(', ');
  }
  return finalTripInfo;
};

const getTripInfo = (bounds) => {
  if (!bounds) {
    return {
      marketingCarrierCodes: null,
      operatingCarrierCodes: null,
      validatingCarrierCodes: null,
      tripType: null,
      cabinClasses: null,
    };
  }
  const tripInfo = bounds.reduce(
    (acc, { segments }) => {
      segments
        .filter((segment) => segment.__typename === 'TripSegment')
        .forEach(
          ({
            marketingCarrier,
            operatingCarrier,
            ticketingCarrier,
            equipmentCode,
            cabinClassName,
          }) => {
            acc.marketingCarrierCodes.push(marketingCarrier.code);
            acc.operatingCarrierCodes.push(operatingCarrier.code);
            acc.validatingCarrierCodes.push(ticketingCarrier?.code);
            acc.tripType.push(equipmentCode);
            acc.cabinClasses.push(cabinClassName);
          },
        );
      return acc;
    },
    {
      marketingCarrierCodes: [],
      operatingCarrierCodes: [],
      validatingCarrierCodes: [],
      tripType: [],
      cabinClasses: [],
    },
  );
  return removeDuplicates(tripInfo);
};

const getOriginDestinationInfo = (tripRoutes) => {
  const destinationAirportIatas = [];
  const originAirportIatas = [];
  const outboundDates = [];
  tripRoutes.forEach((route) => {
    destinationAirportIatas.push(route.destinationCode);
    originAirportIatas.push(route.originCode);
    outboundDates.push(route.departureDate);
  });
  return {
    destinationAirportIatas: destinationAirportIatas.join(', '),
    originAirportIatas: originAirportIatas.join(', '),
    outboundDates: outboundDates.join(', '),
  };
};

const getNumOfSegments = (bounds, isMultiStop, journeyType) => {
  if (!bounds) {
    return {
      inboundNumSegments: null,
      inboundTripDuration: null,
      outboundNumSegments: null,
      outboundTripDuration: null,
    };
  }
  let inboundNumSegments = 0;
  let inboundTripDuration = 0;
  let outboundNumSegments = 0;
  let outboundTripDuration = 0;
  if (journeyType === RETURN) {
    bounds[1].segments.forEach((segment) => {
      if (segment.__typename === 'TripSegment') {
        inboundNumSegments += 1;
      }
      inboundTripDuration += segment.duration;
    });
  }
  if (isMultiStop) {
    bounds.forEach((bound) => {
      bound.segments.forEach((segment) => {
        if (segment.__typename === 'TripSegment') {
          outboundNumSegments += 1;
        }
        outboundTripDuration += segment.duration;
      });
    });
  } else {
    bounds[0].segments.forEach((segment) => {
      if (segment.__typename === 'TripSegment') {
        outboundNumSegments += 1;
      }
      outboundTripDuration += segment.duration;
    });
  }
  return { inboundNumSegments, inboundTripDuration, outboundNumSegments, outboundTripDuration };
};

const isDomestic = (tripRoutes, isMultiStop) => {
  if (isMultiStop) {
    const firstOriginCountryCode = tripRoutes[0].originCode;
    return tripRoutes.every(
      (route) =>
        route.destinationCountryCode === firstOriginCountryCode &&
        route.originCountryCode === firstOriginCountryCode,
    );
  }
  return tripRoutes[0].destinationCountryCode === tripRoutes[0].originCountryCode;
};

const getTripPrice = (travelerPrices) => {
  return travelerPrices
    ? travelerPrices.reduce((acc, { price }) => acc + price?.price?.value, 0)
    : null;
};

export const collectFlightsAndPassengersContextData = (searchData, selectedTripData) => {
  const { cabinClass, date, routes, travelers, tripType: searchDataTripType } = searchData;
  const { adults, childAges } = travelers;
  const {
    marketingCarrierCodes,
    operatingCarrierCodes,
    validatingCarrierCodes,
    tripType,
    cabinClasses,
  } = getTripInfo(selectedTripData?.bounds);
  const isMultiStopTrip =
    Boolean(routes.length > 2) ||
    (routes.length === 2 && routes[0].originCityIata !== routes[1].destinationCityIata);
  const { inboundNumSegments, inboundTripDuration, outboundNumSegments, outboundTripDuration } =
    getNumOfSegments(selectedTripData?.bounds, isMultiStopTrip, searchDataTripType);

  const airSystems = selectedTripData?.systems?.join(', ');
  const bookingClasses = selectedTripData ? cabinClasses : cabinClass;
  const brandedFares = selectedTripData?.brandedFares?.length ?? null;
  const domestic = isDomestic(routes, isMultiStopTrip);
  const isViTrip = selectedTripData?.isVI ?? null;
  const journeyType = searchDataTripType;
  const numTeenChildren = childAges.filter((age) => age > 11 && age < 18).length;
  const numYoungChildren = childAges.filter((age) => age > 1 && age < 12).length;
  const price = getTripPrice(selectedTripData?.travelerPrices);

  return {
    flightData: {
      airSystems,
      bookingClasses: bookingClasses?.toUpperCase(),
      destinationAirportIatas: getOriginDestinationInfo(routes).destinationAirportIatas,
      domestic,
      inboundDates: journeyType === RETURN ? date.return : null,
      inboundNumSegments: inboundNumSegments > 0 ? inboundNumSegments : null,
      inboundTripDuration: inboundTripDuration > 0 ? inboundTripDuration : null,
      isMultiStopTrip,
      isViTrip: isViTrip ? true : null,
      journeyType,
      marketingCarrierCodes: marketingCarrierCodes?.length > 0 ? marketingCarrierCodes : null,
      metaPrice: price,
      offeredBrandedFare: brandedFares === null ? null : Boolean(brandedFares),
      operatingCarrierCodes: operatingCarrierCodes?.length > 0 ? operatingCarrierCodes : null,
      originAirportIatas: getOriginDestinationInfo(routes).originAirportIatas,
      outboundDates:
        journeyType === RETURN ? date.departure : getOriginDestinationInfo(routes).outboundDates,
      outboundNumSegments,
      outboundTripDuration,
      price,
      priceSeenPaymentMethod: null,
      selectedBrandedFare: null,
      selectedFromEntry: null,
      totalAmount: null,
      tripType,
      validatingCarrierCodes: validatingCarrierCodes?.length > 0 ? validatingCarrierCodes : null,
    },
    passengerData: {
      numAdults: adults,
      numChildren: getAgesOfChildren(childAges).length,
      numInfants: getAgesOfInfants(childAges).length,
      numTeenChildren,
      numYoungChildren,
    },
  };
};
